import dynamic from "next/dynamic";
import { useSWRConfig } from "swr";

import { useCallback, useContext, useEffect, useState } from "react";

import classNames from "classnames";
import { filter } from "lodash";

import { faSpinner } from "@fortawesome/free-solid-svg-icons/faSpinner";
import { faTimes } from "@fortawesome/free-solid-svg-icons/faTimes";

import FontAwesomeIcon from "components/FontAwesomeIcon";
import { NotificationContext } from "components/Notification/NotificationContext";
import ProfileSnippet from "components/ProfileSnippet";
import TopNavBarMobile from "components/TopNavBar/TopNavBarMobile";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { ApiEndpoint, baseFetcher, fetchApi } from "utils/baseFetcher";

import styles from "./style.module.scss";

const Modal = dynamic(() => import("react-modal"));

type FollowTeamModalProps = {
  profile: any;
  onFollowAll?: (_isAllFollowed: any) => void;
  onClose: () => void;
};

export const analystsUrl = (slug: string) => {
  const endpoint = new ApiEndpoint(`/api/v2/companies/${slug}/analysts`);
  endpoint.searchParams.append("include", "company,introducer");
  endpoint.searchParams.append("sort", "name");

  return endpoint.href;
};

const FollowTeamModal = ({
  profile,
  onClose,
  onFollowAll,
}: FollowTeamModalProps) => {
  const { slug, analystsCount } = profile;

  const { mobile } = useMediaQuery();

  const { account } = useAuthentication();

  const { data: accounts, mutate } = useApi(analystsUrl(slug), baseFetcher, {
    revalidateFirstPage: false,
    revalidateOnMount: false,
    revalidateOnFocus: false,
  });

  const { setNotification } = useContext(NotificationContext);

  const [following, setFollowing] = useState(false);

  const { cache } = useSWRConfig();

  const close = useCallback(() => {
    onClose();
    cache.delete(analystsUrl(slug));
  }, [cache, onClose, slug]);

  const followAll = useCallback(() => {
    const notFollowed = accounts?.data?.filter?.((a: any) => !a.isFollowed);
    if (!notFollowed.length) {
      close();
      return;
    }
    const data = {
      data: {
        id: account?.id,
        type: "accounts",
        relationships: {
          "following-accounts": {
            data: notFollowed.map(({ id }: any) => {
              return {
                id,
                type: "accounts",
              };
            }),
          },
        },
      },
    };

    setFollowing(true);
    fetchApi(`/api/v2/accounts/${account?.id}/follow-accounts`, {
      method: "PATCH",
      headers: { "content-type": "application/vnd.api+json" },
      body: JSON.stringify(data),
    })
      .then(() => {
        setFollowing(false);
        setNotification("All analysts have been followed.", "success");
        onFollowAll?.(true);
        close();
        // TODO: invalidateInsights();
      })
      .catch(() => {
        setFollowing(false);
        setNotification("Something went wrong!", "error");
      });
  }, [account?.id, accounts?.data, close, onFollowAll, setNotification]);

  const onFollow = useCallback(
    (profile) => {
      mutate({
        ...accounts,
        data: accounts.data.map((a: any) => {
          if (profile.id === a.id) {
            return {
              ...a,
              isFollowed: profile?.isFollowed,
            };
          }

          return a;
        }),
      });
    },
    [accounts, mutate]
  );

  useEffect(() => {
    if (!accounts?.data) return;

    const followedAnalysts = filter(accounts?.data, (item) => {
      return item.isFollowed;
    });

    if (followedAnalysts.length === analystsCount) {
      onFollowAll?.(true);
    } else {
      onFollowAll?.(false);
    }
  }, [accounts?.data, analystsCount, onFollowAll]);

  return (
    <Modal
      portalClassName={styles.modal}
      overlayClassName={styles.overlay}
      className={classNames(
        styles.content,
        mobile ? styles.mobileLayout : styles.desktopLayout
      )}
      isOpen
      onRequestClose={close}
      onAfterOpen={() => {
        document.body.style.overflowY = "hidden";
      }}
      onAfterClose={() => {
        document.body.style.overflowY = "";
      }}
    >
      {mobile ? (
        <TopNavBarMobile onGoBack={close} />
      ) : (
        <div className={styles.actionsBar}>
          <FontAwesomeIcon
            icon={faTimes}
            className={styles.closeButton}
            onClick={close}
          />
        </div>
      )}
      <h4 className="text-[22px] font-light mb-[15px]">
        Follow <span className="font-normal">{profile?.name}</span>
      </h4>
      <div className="min-h-[20vh] flex flex-col divide-x-0 divide-y divide-solid divide-black/20">
        {accounts?.data?.map?.((account: any) => (
          <div key={account.slug}>
            <div className="mx-[-15px]">
              <ProfileSnippet
                profile={account}
                showFollowButton
                full
                onFollow={onFollow}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-[15px] pb-[5px] overflow-hidden">
        <button
          className={classNames(
            "rounded-full px-[1em] py-[0.75em] leading-[1] min-w-[6em] text-active-black",
            "bg-white border border-solid border-active-black hover:bg-active-black hover:text-white",
            "float-left"
          )}
          onClick={close}
        >
          Close
        </button>
        {accounts?.data?.length && (
          <button
            className={classNames(
              "rounded-full px-[1em] py-[0.75em] leading-[1] min-w-[6em]",
              "bg-smart-teal border-none text-white font-medium hover:bg-[#1d8988]",
              "float-right"
            )}
            onClick={followAll}
          >
            {following && (
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin mr-[5px]"
              />
            )}
            Follow All
          </button>
        )}
      </div>
    </Modal>
  );
};

export default FollowTeamModal;
